import { FC } from 'react'

import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import { DATE_FORMAT_LIST } from 'shared/constants/dateFormatList'
import EmptyImage from 'shared/images/empty-image.webp'
import { dateFormat } from 'shared/lib/dateFormat'
import { Button } from 'shared/ui/button'
import { ImageOptimized } from 'shared/ui/image-optimized'

import type { TourDocumentOrderCardProps } from '../../model'

import s from './styles.module.scss'

interface Props {
  order: TourDocumentOrderCardProps
  onOpen: () => void
}

const TOUR_ORDER_STATUS_TRANSLATION_KEYS: Partial<
  Record<TourDocumentOrderCardProps['status'], string>
> = {
  IN_TRIP: 'tourDetails.inTrip',
  COMPLETED: 'tourDetails.completed',
  CANCELLED: 'tourDetails.cancelled',
}

export const TourOrderCard: FC<Props> = ({ order, onOpen }) => {
  const { t } = useTranslation()
  const statusKey =
    TOUR_ORDER_STATUS_TRANSLATION_KEYS[order.status] ?? 'tourDetails.upcoming'
  const { total, paid, remaining } = order
  const hasFinancialData = total !== null && paid !== null && remaining !== null
  const progress =
    total !== null && paid !== null && total > 0
      ? Math.min(100, Math.max(0, (paid / total) * 100))
      : 0
  const format = (value: number) => value.toLocaleString('ru-RU')

  return (
    <article className={s.card}>
      <div className={s.header}>
        <div className={s.heading}>
          <span
            className={clsx(
              s.status,
              order.status === 'CANCELLED' && s.cancelled,
              'semi-bold-12',
            )}
          >
            {t(statusKey)}
          </span>
          <h3 className="extra-bold-16">{order.hotel}</h3>
        </div>
        <ImageOptimized
          src={order.image}
          alt={order.hotel}
          errorImage={EmptyImage}
          width={52}
          height={52}
          wrapperClassName={s.image}
        />
      </div>
      <div className={s.divided}>
        <p className="semi-bold-12">{order.destination}</p>
        <p className={clsx(s.secondary, 'medium-12')}>
          {dateFormat(order.startDate, DATE_FORMAT_LIST.DATE_MONTH_SHORT)}
          {' – '}
          {dateFormat(order.endDate, DATE_FORMAT_LIST.DATE_MONTH_SHORT)},{' '}
          {t('tours_block.night', { count: order.nights })}
          {typeof order.tourists === 'number' && (
            <>
              {', '}
              {t('tours_block.tourists_count', { count: order.tourists })}
            </>
          )}
        </p>
      </div>
      {hasFinancialData && (
        <div className={s.divided}>
          <div className={s.row}>
            <span className="semi-bold-12">{t('tourDetails.price')}</span>
            <span className="bold-12">
              {format(total)}
              <u>с</u>
            </span>
          </div>
          {remaining > 0 ? (
            <>
              <div
                className={s.progress}
                role="progressbar"
                aria-label={t('tourPaymentHistory.paid')}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={progress}
              >
                <div style={{ width: `${progress}%` }} />
              </div>
              <div className={clsx(s.row, s.amounts, 'medium-12')}>
                <div>
                  <p className={s.secondary}>{t('tourPaymentHistory.paid')}</p>
                  <p>
                    {format(paid)}
                    <u>с</u>
                  </p>
                </div>
                <div>
                  <p className={s.secondary}>
                    {t('tourOrderPreview.remaining')}
                  </p>
                  <p>
                    {format(remaining)}
                    <u>с</u>
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className={clsx(s.row, s.secondary, 'medium-12')}>
              <span>{t('tourDetails.status')}</span>
              <span>{t('tourPaymentHistory.paid')}</span>
            </div>
          )}
        </div>
      )}
      <Button
        variant="grey"
        className={clsx(s.button, 'semi-bold-14')}
        onClick={onOpen}
      >
        {t('tourOrderPreview.details')}
      </Button>
    </article>
  )
}
